import React, { useState, useEffect } from "react";
import { clone, cloneDeep, get, isEmpty } from "lodash";
import styled from "styled-components";

import { Preset, IntroConfig } from "../models/IntroConfig";
import { ModelItem, ModelItemType, ModelItemsMap } from "../models/ModelItem";

import { ImagesContainer } from "./ImagesContainer";
import { ButtonBack } from "./ButtonBack";

import { getImageUrlForConfig } from "../utils/dataUtils";
import { goToConfigurator } from "../utils/navigationUtils";
import GaReporter from "../utils/gaReporter";

const StepNameContainer = styled.div`
    position: absolute;
    z-index: 2;
    top: 25px;
    left: 25px;
    min-width: 200px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 600px) and (orientation: landscape) {
        max-width: 160px;
        min-width: "unset";
    }
`;

const StepName = styled.div`
    font-size: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 250px;
    height: 50px;
    border-radius: 8px;
    color: white;
    background: ${({ theme }) => get(theme, "modelSelector.step.backgroundColor", "var(--primary-color)")};
    @media (max-width: 600px) and (orientation: landscape) {
        font-size: 16px;
    }
`;

export const ModelSelector = ({
    composedUrl,
    guiResourcesUrl,
    introConfig,
    accountId,
}: {
    composedUrl: string;
    guiResourcesUrl: string;
    introConfig: IntroConfig;
    accountId: string | null;
}) => {
    const [items, setItems]: [ModelItemsMap, Function] = useState({});
    const [, setLogo] = useState("");
    const [stepTexts, setStepTexts] = useState<Array<string>>([]);
    const [stepIndex, setStepIndex] = useState(0);
    const [selectedItem, setSelectedItem] = useState("root");
    const [breadcrumbs, setBreadcrumbs] = useState<Array<string>>([]);
    const [requestQueue, setRequestQueue] = useState<Array<{ id: string; sets: Preset[] }>>([]);
    const [currentRequest, setCurrentRequest] = useState<{ id: string; sets: Preset[] } | null>(null);

    // Initialize things when the introConfig is set.
    useEffect(() => {
        if (!introConfig) return;

        // Build the "requests" for the preset images.
        // Traverse breadth first to request all images
        // for level 2 before any level 3 and so on.
        const newRequestQueue: Array<{ id: string; sets: Preset[] }> = [];
        newRequestQueue.push({ id: "root", sets: introConfig.sets });
        for (let i = 0; i < newRequestQueue.length; i += 1) {
            const request = newRequestQueue[i];
            request.sets.forEach((set, j) => {
                if (set.sets) {
                    newRequestQueue.push({ id: `${request.id}_${j}`, sets: set.sets });
                }
            });
        }
        setRequestQueue(newRequestQueue);

        setStepTexts(introConfig.stepTexts);
        setLogo(guiResourcesUrl + introConfig.logo);
    }, [introConfig, guiResourcesUrl]);

    // Request the image URLs for one preset level at a time.
    useEffect(() => {
        if (isEmpty(requestQueue) || currentRequest) return;

        const getImageUrl = async (config: string, serieName: string, width: number, height: number) => {
            let imageUrl = null;
            try {
                imageUrl = await getImageUrlForConfig(config, serieName, composedUrl, width, height, accountId);

                return imageUrl;
            } catch (e) {
                return null;
            }
        };

        const getItems = async (presets: Array<Preset>, parent: string): Promise<ModelItem[]> => {
            let height = Math.ceil(presets.length > 3 ? (window.innerHeight - 8) / 2 : window.innerHeight);
            let width = 0;
            if (window.devicePixelRatio) {
                height = Math.round(height * window.devicePixelRatio);
            }

            if (window.innerWidth <= 1024) {
                height = 0;
                width = Math.round(window.innerWidth * window.devicePixelRatio);
                if (width % 2 === 1) {
                    width = 2 * Math.round(width / 2);
                }
            }

            const result: (ModelItem | null)[] = await Promise.all(
                presets.map(async (preset, i) => {
                    const imageUrl = await getImageUrl(preset.config, preset.serie, width, height);
                    if (!imageUrl) return null;

                    return {
                        id: `${parent}_${i}`,
                        imageUrl,
                        config: preset.sets ? null : preset.config,
                        name: preset.name,
                        type: preset.sets ? ModelItemType.MAIN : ModelItemType.TEMPLATES,
                    };
                })
            );

            return result.filter((x): x is ModelItem => x != null);
        };

        const requestNextImages = async () => {
            let index = 0;
            if (selectedItem) {
                // Prioritize loading images for the selected item,
                // and then children of current item.
                index = requestQueue.findIndex(x => x.id === selectedItem);
                if (index === -1) {
                    index = requestQueue.findIndex(x => x.id.indexOf(selectedItem) === 0);
                }
                if (index === -1) index = 0;
            }

            const newRequestQueue = clone(requestQueue);
            const request = newRequestQueue.splice(index, 1)[0];
            setRequestQueue(newRequestQueue);
            setCurrentRequest(request);

            const subItems = await getItems(request.sets, request.id);
            const itemsUpdated = clone(items);
            itemsUpdated[request.id] = subItems;
            setItems(itemsUpdated);
            setCurrentRequest(null);
        };
        requestNextImages();
    }, [requestQueue, currentRequest, composedUrl, items, selectedItem, accountId]);

    const onItemClick = (itemId: string, itemName: string, config: string) => {
        if (config) {
            GaReporter.event(`model_selector_step${stepIndex + 1}`, "ModelSelector", itemName, {
                event_callback: GaReporter.createHitCallback(() => goToConfigurator(config)),
            });
        } else {
            GaReporter.event(`model_selector_step${stepIndex + 1}`, "ModelSelector", itemName);
            const breadcrumbsUpdated: Array<string> = cloneDeep(breadcrumbs);
            breadcrumbsUpdated.push(itemId);
            const newStepIndex = stepIndex + 1;

            setSelectedItem(itemId);
            setBreadcrumbs(breadcrumbsUpdated);
            setStepIndex(newStepIndex);
        }
    };

    const onBackClick = () => {
        const breadcrumbsUpdated: Array<string> = breadcrumbs.slice(0, breadcrumbs.length - 1);
        const newStepIndex = Math.max(0, stepIndex - 1);

        setSelectedItem(breadcrumbsUpdated[breadcrumbsUpdated.length - 1] || "root");
        setBreadcrumbs(breadcrumbsUpdated);
        setStepIndex(newStepIndex);
    };

    return (
        <>
            {stepIndex < stepTexts.length ? (
                <StepNameContainer>
                    <StepName>
                        <span>{stepTexts[stepIndex]}</span>
                    </StepName>
                </StepNameContainer>
            ) : (
                <></>
            )}
            <ImagesContainer key={selectedItem} items={items[selectedItem]} onItemClick={onItemClick} />
            {stepIndex === 0 ? <></> : <ButtonBack onClick={onBackClick} />}
        </>
    );
};
